import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, PerspectiveLines } from "@/components/Reveal";
import { Showreel } from "@/components/Showreel";
import { Marquee } from "@/components/Marquee";

const HERO_BG =
  "https://images.unsplash.com/photo-1565626424178-c699f6601afd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJydXRhbGlzdCUyMGFyY2hpdGVjdHVyZSUyMHNoYWRvd3xlbnwwfHx8fDE3ODYzNjMzOTd8MA&ixlib=rb-4.1.0&q=85";

const services = [
  { n: "01", title: "Strategic Communications", desc: "Positioning, messaging, reputation management and stakeholder communications aligned with organisational objectives.", span: "sm:col-span-3" },
  { n: "02", title: "Public Relations & Media", desc: "Strategic media relations, earned media, corporate profiling, editorial campaigns and reputation building.", span: "sm:col-span-3" },
  { n: "03", title: "Thought Leadership", desc: "Positioning leaders and institutions through op-eds, interviews, Q&As, expert commentary and insight-led storytelling.", span: "sm:col-span-2" },
  { n: "04", title: "Brand & Creative", desc: "Brand narratives, creative campaigns, digital content, design and audio-visual storytelling.", span: "sm:col-span-2" },
  { n: "05", title: "Events & Experiences", desc: "Conferences, launches, stakeholder engagement and experiences designed to create visibility and meaningful connections.", span: "sm:col-span-2" },
];

const clients = ["UNICEF", "WHO-SEARO", "UN World Food Programme", "WWF-India", "Bharti Foundation"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="h-[120%] w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-background/60" />
      </motion.div>

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-[1600px] px-6 pb-24 pt-40 sm:px-12">
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, ease: [0.19, 1.0, 0.22, 1.0], delay: 0.1 }}
            className="mb-8 text-xs uppercase tracking-[0.3em] text-primary"
            data-testid="hero-eyebrow"
          >
            Strategic Communications · Creative Thinking · Meaningful Influence
          </motion.p>
        </div>

        <PerspectiveLines
          lines={["Ideas that matter.", "Stories that move.", <><span className="text-primary">Influence</span> that lasts.</>]}
          className="font-syne text-[1.75rem] font-extrabold uppercase leading-[1.45] tracking-wider sm:text-[2.4rem] lg:text-[3.9rem]"
          lineClassName="will-change-transform"
          baseDelay={0.3}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut", delay: 1.1 }}
          className="mt-12 flex max-w-2xl flex-col gap-8 sm:flex-row sm:items-end sm:justify-between"
        >
          <p className="text-sm leading-relaxed text-foreground/80 sm:text-base" data-testid="hero-subcopy">
            A strategic communications and brand advisory firm helping organisations, institutions and leaders build
            reputation, relevance and influence in an increasingly connected world.
          </p>
          <Link
            to="/contact"
            data-testid="hero-cta-button"
            className="group flex w-max shrink-0 items-center gap-3 bg-primary px-8 py-4 font-syne text-sm font-bold uppercase tracking-[0.15em] text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            Begin a Conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

const ServicesIndex = () => (
  <section className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 sm:py-32" data-testid="home-services-section">
    <Reveal>
      <div className="mb-24 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h2 className="font-syne text-3xl font-extrabold uppercase tracking-wide sm:text-4xl lg:text-5xl">
          Our <span className="text-primary">disciplines</span>
        </h2>
        <Link to="/services" data-testid="home-services-link" className="group flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground transition-colors duration-300 hover:text-primary">
          View all disciplines <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </Reveal>
    <div className="space-y-[120px]">
      {services.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.05}>
          <Link to="/services" data-testid={`home-service-card-${s.n}`} className="group block max-w-4xl">
            <span className="font-syne text-xs font-bold uppercase tracking-[0.35em] text-primary">{s.n}</span>
            <h3 className="mt-6 font-syne text-3xl font-extrabold uppercase leading-[1.05] tracking-wide transition-colors duration-300 group-hover:text-primary sm:text-5xl">
              {s.title}
            </h3>
            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">{s.desc}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const ClientStrip = () => (
  <section className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 sm:py-32" data-testid="home-clients-section">
    <Reveal>
      <p className="mb-4 text-xs uppercase tracking-[0.3em] text-muted-foreground">Our experience includes work with</p>
      <h2 className="font-syne text-3xl font-extrabold uppercase tracking-wide sm:text-4xl lg:text-5xl">
        Trusted by institutions <span className="text-stroke">that shape the world</span>
      </h2>
    </Reveal>
    <div className="mt-16 divide-y divide-black/10 border-y border-black/10">
      {clients.map((c, i) => (
        <Reveal key={c} delay={i * 0.06}>
          <div className="group flex items-baseline justify-between gap-6 py-6 sm:py-8" data-testid={`home-client-${i}`}>
            <span className="font-syne text-2xl font-extrabold uppercase tracking-tight transition-colors duration-300 group-hover:text-primary sm:text-4xl lg:text-5xl">
              {c}
            </span>
            <span className="font-syne text-sm text-muted-foreground">0{i + 1}</span>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal delay={0.2}>
      <p className="mt-10 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
        Creative Curve holds a <span className="text-primary">Long-Term Agreement with the UN World Food Programme</span>,
        reflecting our experience working with the communication standards and requirements of major international
        institutions.
      </p>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee items={["Ideas that matter", "Stories that move", "Influence that lasts"]} />

      <section className="mx-auto max-w-[1600px] px-6 py-24 sm:px-12 sm:py-32" data-testid="home-intro-section">
        <Reveal>
          <p className="max-w-4xl text-lg leading-relaxed text-foreground/90 sm:text-2xl">
            We work at the intersection of <span className="text-primary">strategy, storytelling and influence</span> —
            transforming ideas, expertise and organisational purpose into communication that connects with audiences
            and creates lasting impact.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Our approach goes beyond conventional public relations. We combine strategic counsel, creative thinking and
            editorial expertise to develop narratives that are credible, distinctive and culturally relevant.
          </p>
        </Reveal>
      </section>

      <Showreel />
      <ServicesIndex />
      <ClientStrip />
    </main>
  );
}
